import { AlertTriangle, BadgeInfo, CloudCheck, CloudSync, Plus, SquareDashedText, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getTaskTypeLabel, getTaskTypeTooltip, TaskType } from '../../../../../../db/api/task-types/task-main.js';
import { shouldShowOcrText } from '../../../../../../shared/bounding-box-content.js';
import PageNotFoundImage from '../../../../../assets/page-not-found.svg';
import { cn } from '../../../../../lib/utils.js';
import { MarkdownWithMath } from '../../../../markdown/markdown-with-math.js';
import { Button } from '../../../../ui/button.js';
import { LatexRichEditor } from '../../../../ui/latex-rich-editor.js';
import type { StructureStateStore } from '../hooks/use-structure-state.js';
import {
  computeTaskTotal,
  DEFAULT_CARD_HEIGHT_PX,
  FALLBACK_PAGE_ASPECT_RATIO,
  findFlatPointInconsistencies,
  getConfidenceCardClasses,
  MIN_CARD_GAP_PX,
  PAGE_RAIL_GAP_PX,
  type StructureEditorTask,
} from '../utils.js';
import { BoxOverlay } from './box-overlay.js';

/**
 * The points input(s)/badge for a task, shared by the on-page cards and the box-less card list. A leaf or
 * task frame shows a single field (a leaf's own points, or a task frame's total — dynamic Σ when derived).
 * An answerable PARENT shows BOTH: an editable OWN field (its own answer, = total − children) and the
 * TOTAL field, so the teacher can enter either. The total stays editable even when dynamic (editing
 * re-fixes it). Missing/mismatch fields render red and stay visible even unfocused.
 */
function TaskPointsField({
  store,
  task,
  editableWhenIdle,
  missing,
}: {
  store: StructureStateStore;
  task: StructureEditorTask;
  editableWhenIdle: boolean;
  missing: boolean;
}) {
  const { t } = useTranslation();
  const { state, actions } = store;
  const isTaskFrame = task.needsAnswer === false;
  const declared = typeof task.declaredMaxPoints === 'number' ? task.declaredMaxPoints : undefined;
  const directChildren = state.tasks.filter(
    other => other.path.length === task.path.length + 1 && task.path.every((seg, i) => other.path[i] === seg),
  );
  const hasChildren = directChildren.length > 0;
  const childValues = directChildren.map(child => computeTaskTotal(child, state.tasks));
  const anyChildPointed = childValues.some(value => value !== undefined);
  const childSum = childValues.reduce<number>((acc, value) => acc + (value ?? 0), 0);
  const isAnswerableParent = !isTaskFrame && hasChildren;
  // An answerable parent's OWN points behave like an extra leaf child: an editable number that counts into
  // the parent's sum. (Task frame own = 0.) 0 means "no points assigned" — a task never has 0 points — so a
  // 0 (or less) counts as UNSET for display/logic. The raw typed value is bound to the input separately so
  // 0.5 can still be typed; a committed 0 is dropped on save and cleared by the merge.
  const own =
    isAnswerableParent && typeof task.ownPoints === 'number' && task.ownPoints > 0 ? task.ownPoints : undefined;
  const ownIsSet = own !== undefined;
  // The TOTAL field runs the SAME fixed↔dynamic cycle for a task frame and an answerable parent. The dynamic
  // sum = own (0 for a task frame) + children. A FIXED declared total is shown (and editable) until the sum
  // hits it exactly — then `reconcileMatchedTaskFrames` drops the declared so it goes dynamic and moves with
  // the sum. Editing the total re-fixes it (updateTaskMaxPoints excludes it from that reconcile).
  const dynamicSum = (own ?? 0) + childSum;
  const anythingPointed = anyChildPointed || ownIsSet;
  // Dynamic as soon as own + subtask sum equals the declared total — even if not all children are assigned
  // yet (the total then tracks the sum). A fixed total that doesn't match the sum stays fixed (and flags).
  const matched = declared != null && Math.abs(dynamicSum - declared) < 0.001;
  const dynamic = hasChildren && ((declared == null && anythingPointed) || matched);
  const shownTotal = dynamic ? dynamicSum : declared;
  const showEditable = !state.isReadOnly && (editableWhenIdle || missing);
  const totalTitle = hasChildren ? t('Canvas.structureEditor.totalPoints') : t('Canvas.msgs.maxPoints');

  const inputClass = (isRed: boolean) =>
    cn(
      'w-11 rounded border bg-white dark:bg-secondary px-1 py-0.5 text-[10px] focus:outline-none',
      isRed
        ? 'border-red-400 ring-1 ring-red-300 text-red-700 dark:text-red-300 focus:border-red-500'
        : 'border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 focus:border-sky-400',
    );
  const badgeClass =
    'rounded bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400';

  const ownField = !isAnswerableParent ? null : showEditable ? (
    <input
      key="own"
      type="number"
      min={0}
      step={0.5}
      value={typeof task.ownPoints === 'number' ? task.ownPoints : ''}
      placeholder="P"
      title={t('Canvas.structureEditor.ownPoints')}
      onPointerDown={event => event.stopPropagation()}
      onClick={event => event.stopPropagation()}
      onChange={event => actions.updateTaskOwnPoints(task.id, event.target.value)}
      className={inputClass(missing)}
    />
  ) : typeof own === 'number' ? (
    <span key="own" title={t('Canvas.structureEditor.ownPoints')} className={cn(badgeClass, 'hidden @[14rem]:inline')}>
      {own} P
    </span>
  ) : null;

  // The total field is ALWAYS editable; it shows the fixed declared value when fixed, or the dynamic sum
  // when dynamic. Editing it re-fixes it (so it no longer snaps back to the sum).
  const totalValue = shownTotal;
  const totalEditable = showEditable;
  const totalField = totalEditable ? (
    <input
      key="total"
      type="number"
      min={0}
      step={0.5}
      value={typeof totalValue === 'number' ? totalValue : ''}
      placeholder={hasChildren ? '' : 'P'}
      title={totalTitle}
      onPointerDown={event => event.stopPropagation()}
      onClick={event => event.stopPropagation()}
      onChange={event => actions.updateTaskMaxPoints(task.id, event.target.value)}
      className={inputClass(missing)}
    />
  ) : typeof totalValue === 'number' && totalValue > 0 ? (
    <span key="total" title={totalTitle} className={cn(badgeClass, 'hidden @[10rem]:inline')}>
      {totalValue} P
    </span>
  ) : null;

  if (!ownField && !totalField) return null;
  return (
    <span className="flex items-center gap-1 shrink-0">
      {ownField}
      {hasChildren && totalField && (
        <span className="hidden @[10rem]:inline text-[9px] font-semibold text-slate-400">Σ</span>
      )}
      {totalField}
    </span>
  );
}

/**
 * The card header row shared by the on-page task cards and the box-less "tasks without a region" cards, so
 * both have the SAME editing (editable path segments + task-type dropdown + points) and idle interface.
 * `isEditable` toggles edit vs read-only display; `onDelete` (if given) renders the trash button.
 */
function TaskCardHeader({
  store,
  task,
  isEditable,
  missing,
  visibleTaskTypes,
  onDelete,
}: {
  store: StructureStateStore;
  task: StructureEditorTask;
  isEditable: boolean;
  missing: boolean;
  visibleTaskTypes: TaskType[];
  onDelete?: () => void;
}) {
  const { t } = useTranslation();
  const { state, actions } = store;
  const [typeMenuOpen, setTypeMenuOpen] = useState(false);
  const normalizeTaskTypes = (taskTypes: string[] | undefined) =>
    (Array.isArray(taskTypes) ? taskTypes : []).filter(taskType => taskType !== TaskType.DEFAULT);
  const activeTaskTypes = normalizeTaskTypes(task.taskType);
  const typeLabel =
    activeTaskTypes.length > 0
      ? activeTaskTypes.map(taskType => getTaskTypeLabel(taskType as TaskType, t)).join(', ')
      : getTaskTypeLabel(TaskType.DEFAULT, t);

  return (
    <div className={cn('flex items-center min-w-0', !isEditable && '@container overflow-hidden')}>
      {isEditable ? (
        <div className="flex items-center gap-1 text-[11px] font-semibold text-slate-700 dark:text-slate-300">
          <span>{t('Canvas.structureEditor.task')}</span>
          {(() => {
            const path = Array.isArray(task.path) && task.path.length > 0 ? task.path : [];
            const inputs = [] as any[];
            for (let i = 0; i <= path.length; i++) {
              const val = path[i] ?? '';
              inputs.push(<span key={`sep-${i}`}> {i > 0 ? '.' : ''}</span>);
              inputs.push(
                <input
                  key={`${task.id}-p-${i}`}
                  value={val}
                  maxLength={6}
                  onPointerDown={event => event.stopPropagation()}
                  onClick={event => event.stopPropagation()}
                  onChange={event => actions.updateTaskPath(task.id, i, event.target.value)}
                  style={{ width: `${Math.max(2, String(val ?? '').length + 1)}ch` }}
                  className="rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary px-0.5 py-0.5 text-[11px] text-slate-700 dark:text-slate-300 focus:border-sky-400 focus:outline-none"
                />,
              );
            }
            return inputs;
          })()}
        </div>
      ) : (
        <span className="min-w-0 shrink text-[11px] font-semibold text-slate-700 dark:text-slate-300 truncate">
          {t('Canvas.structureEditor.task')} {Array.isArray(task.path) ? task.path.join('.') : ''}
        </span>
      )}
      {!isEditable && <div className="grow" aria-hidden />}
      <div className="flex items-center gap-0.5 min-w-0">
        <TaskPointsField store={store} task={task} editableWhenIdle={isEditable} missing={missing} />
        {isEditable ? (
          <div className="relative overflow-visible">
            <button
              type="button"
              onPointerDown={event => event.stopPropagation()}
              onClick={event => {
                event.stopPropagation();
                setTypeMenuOpen(current => !current);
              }}
              className="flex items-center gap-1 rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-slate-600 shadow-sm focus:border-sky-400 focus:outline-none"
            >
              <span>{typeLabel}</span>
              <span className="text-slate-400">▾</span>
            </button>
            {typeMenuOpen && (
              // das muss wieder multiselect werden für multitask handling is schon drin
              <div
                onPointerDown={event => event.stopPropagation()}
                onClick={event => event.stopPropagation()}
                className="absolute right-0 top-full z-50 mt-1 w-max min-w-[8rem] max-w-[95cqw] max-h-60 overflow-y-auto overscroll-contain rounded border border-slate-200 bg-white p-2 shadow-xl"
              >
                <div className="flex flex-col gap-1">
                  {visibleTaskTypes.map(taskType => {
                    const checked = (normalizeTaskTypes(task.taskType) as TaskType[]).includes(taskType);
                    return (
                      <label
                        key={taskType}
                        title={getTaskTypeTooltip(taskType, t)}
                        className="flex items-center gap-2 rounded px-1 py-0.5 text-[10px] font-medium text-slate-700 hover:bg-slate-50"
                      >
                        <input
                          type="radio"
                          name={`task-type-${task.id}`}
                          checked={checked}
                          onChange={() => {
                            actions.updateTaskType(task.id, [taskType]);
                            setTypeMenuOpen(false);
                          }}
                          className="h-3.5 w-3.5 border-slate-300 text-sky-600 focus:ring-sky-500"
                        />
                        <span>{getTaskTypeLabel(taskType, t)}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ) : (
          <span className="min-w-0 truncate rounded bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">
            {typeLabel}
          </span>
        )}
        {!state.isReadOnly && onDelete && (
          <button
            type="button"
            className="shrink-0 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary p-1 text-slate-600 dark:text-slate-400 transition-colors hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600"
            onPointerDown={event => {
              event.preventDefault();
              event.stopPropagation();
              onDelete();
            }}
            title={t('Canvas.structureEditor.deleteBbx')}
          >
            <Trash2 size={11} />
          </button>
        )}
      </div>
    </div>
  );
}

export function StructureTopBar({ store }: { store: StructureStateStore }) {
  const { t } = useTranslation();
  const { state, actions } = store;
  const isCompact = state.containerSize.width > 0 && state.containerSize.width < 480;
  const pointInconsistencies = findFlatPointInconsistencies(state.tasks);

  return (
    <div className="shrink-0 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-card px-4 py-3 shadow-sm">
      <div>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {t('Canvas.structureEditor.taskAreasDetected', { count: state.tasks.length })}
        </p>
      </div>

      <div className="flex items-center gap-2">
        {!state.isReadOnly && (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={actions.handleAddTask}
              title={t('Canvas.structureEditor.task')}
              className={isCompact ? 'px-2' : 'gap-1.5'}
            >
              {isCompact ? (
                <SquareDashedText className="h-4 w-4" />
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  {t('Canvas.structureEditor.task')}
                </>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={actions.handleAddAdditionalInfo}
              title={t('Canvas.structureEditor.additionalInfo')}
              className={isCompact ? 'px-2' : 'gap-1.5'}
            >
              {isCompact ? (
                <BadgeInfo className="h-4 w-4" />
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  {t('Canvas.structureEditor.additionalInfo')}
                </>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={actions.handleRemoveSelected}
              disabled={!state.selectedTaskId && !state.selectedAdditionalInfoId}
              className={isCompact ? 'px-2' : 'gap-1.5'}
            >
              <Trash2 className="h-4 w-4" />
              {!isCompact && t('Canvas.structureEditor.remove')}
            </Button>
          </>
        )}

        {state.isReadOnly ? (
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs shadow-sm select-none">
            <span className="font-semibold">{t('Canvas.structureEditor.readOnly')}</span>
          </div>
        ) : state.isSaving ? (
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs shadow-sm select-none">
            <CloudSync className="w-[15px] h-[15px] animate-spin" />
            <span className="font-semibold">{t('Canvas.structureEditor.saving')}</span>
          </div>
        ) : (
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs shadow-sm select-none">
            <CloudCheck className="w-[15px] h-[15px]" />
            <span className="font-semibold">{t('Canvas.structureEditor.saved')}</span>
          </div>
        )}
      </div>

      {pointInconsistencies.length > 0 && (
        <div className="basis-full w-full rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-xs text-red-700 dark:text-red-300">
          <div className="flex items-start gap-1.5">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="flex flex-col gap-1">
              <span className="font-semibold">{t('Canvas.structureEditor.pointsInconsistent')}</span>
              <div className="flex flex-wrap gap-1">
                {pointInconsistencies.map(issue => (
                  <span
                    key={issue.taskId}
                    className="rounded bg-red-100 dark:bg-red-900/40 px-1.5 py-0.5 font-mono font-semibold text-red-700 dark:text-red-300"
                  >
                    {issue.path.join('.')}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function StructurePageRenderer({
  store,
  leftPanelWidth,
}: {
  store: StructureStateStore;
  leftPanelWidth: number;
}) {
  const { t } = useTranslation();
  const { state, computed, actions, refs, props } = store;
  const inconsistentTaskIds = new Set(findFlatPointInconsistencies(state.tasks).map(issue => issue.taskId));
  // Box-less cards have no bounding box to anchor the shared selection to, so they track their own
  // click-to-edit state here (otherwise they'd all render in edit mode at once).
  const [editingBoxlessId, setEditingBoxlessId] = useState<string | null>(null);

  const visibleTaskTypes = computed.taskTypes.filter(taskType => getTaskTypeLabel(taskType, t).trim().length > 0);

  const pagesWithLayout = props.pages.map((pageUrl, pageIndex) => {
    const pageBoxes = computed.currentPageBoxes.filter(item => item.box.pageNumber === pageIndex);
    const orderedPageBoxes = [...pageBoxes].sort((left, right) => left.box.y - right.box.y);
    const pageAspectRatio = state.pageAspectRatios[pageIndex] || FALLBACK_PAGE_ASPECT_RATIO;

    const availableWidth = state.containerSize.width > 0 ? state.containerSize.width : 1000;
    const imageWidthPx = (availableWidth * leftPanelWidth) / 100;
    const pageHeightPx = imageWidthPx / pageAspectRatio;

    const cardGapPercent = Math.max((MIN_CARD_GAP_PX / pageHeightPx) * 100, 0.6);
    let previousCardBottom = Number.NEGATIVE_INFINITY;

    const positionedPageBoxes = orderedPageBoxes.map(item => {
      const measuredCardHeightPx = Math.max(state.cardHeightsPx[item.boxKey] ?? DEFAULT_CARD_HEIGHT_PX, 44);
      const cardHeightPercent = Math.max((measuredCardHeightPx / pageHeightPx) * 100, 4);
      const preferredTop = item.box.y;
      const cardTop =
        previousCardBottom === Number.NEGATIVE_INFINITY
          ? preferredTop
          : Math.max(preferredTop, previousCardBottom + cardGapPercent);
      previousCardBottom = cardTop + cardHeightPercent;
      return { ...item, cardTop };
    });

    return {
      pageUrl,
      pageIndex,
      pageHeightPx,
      positionedPageBoxes,
    };
  });

  return (
    <div ref={refs.scrollContainerRef} className="h-full p-2" onPointerDown={actions.clearSelection}>
      <div className="flex h-full w-full min-w-0 items-start" style={{ gap: `${PAGE_RAIL_GAP_PX}px` }}>
        <div
          className="h-full overflow-y-auto overflow-x-hidden"
          style={{ width: `calc(${leftPanelWidth}% - ${PAGE_RAIL_GAP_PX / 2}px)` }}
          onWheelCapture={event => event.stopPropagation()}
          onPointerDown={event => event.stopPropagation()}
        >
          <div ref={refs.imageContainerRef} className="flex flex-col gap-4 items-start">
            {pagesWithLayout.map(({ pageUrl, pageIndex, positionedPageBoxes }) => (
              <div key={pageIndex} className="relative w-full min-w-0 bg-transparent">
                {pageIndex > 0 && <div className="mb-2 h-px w-full bg-slate-300/80" />}

                <div className="relative w-full bg-white dark:bg-slate-800 shadow-xl">
                  <img
                    src={pageUrl}
                    alt={t('Canvas.structureEditor.blankExamPage', { page: pageIndex + 1 })}
                    className="block w-full select-none pointer-events-none"
                    onLoad={event => {
                      const { naturalWidth, naturalHeight } = event.currentTarget;
                      if (!naturalWidth || !naturalHeight) return;
                      const ratio = naturalWidth / naturalHeight;
                      actions.setPageAspectRatios(prev => {
                        if (prev[pageIndex] === ratio) return prev;
                        return { ...prev, [pageIndex]: ratio };
                      });
                    }}
                    onError={event => {
                      event.currentTarget.src = PageNotFoundImage;
                    }}
                  />

                  {positionedPageBoxes.map(item => {
                    const isFocused = state.selectedBoxKey === item.boxKey;

                    if (item.kind === 'task') {
                      const { task, box, boxKey } = item;
                      return (
                        <div key={boxKey}>
                          <BoxOverlay
                            box={box}
                            selected={isFocused}
                            isReadOnly={state.isReadOnly}
                            color="blue"
                            onSelect={() => {
                              actions.selectBox(task.id, boxKey);
                              refs.cardRefsMap.current
                                .get(boxKey)
                                ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                            }}
                            onInteractionEnd={(boxId, didChange) => {
                              if (state.isReadOnly || !didChange) return;
                              const currentTask = refs.tasksRef.current.find(t => t.id === task.id);
                              const currentBox = currentTask?.boundingBoxes?.find(b => b.id === boxId);
                              if (currentBox?.approved !== true) {
                                actions.requestPersist(refs.tasksRef.current, { rerunDescriptionOCRBoxIds: [boxId] });
                              } else {
                                actions.requestPersist(refs.tasksRef.current);
                              }
                            }}
                            onDelete={() => actions.removeBox(task.id, box.id)}
                            onRetry={
                              // Approved boxes are skipped by the OCR-rerun mutation, so retry would
                              // be a no-op — don't offer it for those.
                              box.approved === true
                                ? undefined
                                : () => {
                                    if (state.isReadOnly) return;
                                    actions.requestPersist(refs.tasksRef.current, {
                                      rerunDescriptionOCRBoxIds: [box.id],
                                    });
                                  }
                            }
                            onChange={nextBox => {
                              if (state.isReadOnly) return;
                              actions.setTasks(prev => {
                                const next = prev.map(t =>
                                  t.id === task.id
                                    ? {
                                        ...t,
                                        boundingBoxes: t.boundingBoxes.map(existing =>
                                          existing.id === box.id
                                            ? { ...nextBox, imageId: null, approved: false }
                                            : existing,
                                        ),
                                      }
                                    : t,
                                );
                                refs.tasksRef.current = next;
                                return next;
                              });
                            }}
                          />
                        </div>
                      );
                    }

                    const { info, box, boxKey } = item;
                    return (
                      <div key={boxKey}>
                        <BoxOverlay
                          box={box}
                          selected={isFocused}
                          isReadOnly={state.isReadOnly}
                          color="purple"
                          onSelect={() => {
                            actions.selectAdditionalInfoBox(info.id, boxKey);
                            refs.cardRefsMap.current
                              .get(boxKey)
                              ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                          }}
                          onInteractionEnd={(_, didChange) => {
                            if (state.isReadOnly || !didChange) return;
                            actions.requestAdditionalInfoPersist(refs.localAdditionalInfoRef.current);
                          }}
                          onDelete={() => actions.removeAdditionalInfoBox(info.id, box.id)}
                          onRetry={() => {
                            if (state.isReadOnly) return;
                            const next = refs.localAdditionalInfoRef.current.map(i =>
                              i.id === info.id
                                ? {
                                    ...i,
                                    boundingBoxes: i.boundingBoxes.map(b =>
                                      b.id === box.id ? { ...b, imageId: null, approved: false } : b,
                                    ),
                                  }
                                : i,
                            );
                            refs.localAdditionalInfoRef.current = next;
                            actions.setLocalAdditionalInfo(next);
                            actions.requestAdditionalInfoPersist(next);
                          }}
                          onChange={nextBox => {
                            if (state.isReadOnly) return;
                            actions.setLocalAdditionalInfo(prev => {
                              const next = prev.map(i =>
                                i.id === info.id
                                  ? {
                                      ...i,
                                      boundingBoxes: i.boundingBoxes.map(b =>
                                        b.id === box.id ? { ...nextBox, imageId: null, approved: false } : b,
                                      ),
                                    }
                                  : i,
                              );
                              refs.localAdditionalInfoRef.current = next;
                              return next;
                            });
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div
          className="h-full overflow-y-auto overflow-x-hidden"
          style={{ width: `calc(${100 - leftPanelWidth}% - ${PAGE_RAIL_GAP_PX / 2}px)` }}
          onWheelCapture={event => event.stopPropagation()}
          onPointerDown={event => event.stopPropagation()}
        >
          <div className="flex flex-col gap-4 items-start">
            {(() => {
              // Tasks with no bounding box would otherwise never render as a card — surface them here so
              // their points/structure stay editable (e.g. a task frame the agent inferred without a header).
              const boxless = state.tasks.filter(task => task.boundingBoxes.length === 0);
              if (boxless.length === 0) return null;
              return (
                <div className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white/90 dark:bg-card/90 p-2 flex flex-col gap-1.5">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {t('Canvas.structureEditor.tasksWithoutRegion')}
                  </span>
                  {boxless.map(task => {
                    const isEditing = !state.isReadOnly && editingBoxlessId === task.id;
                    return (
                      <div
                        key={task.id}
                        onPointerDown={event => event.stopPropagation()}
                        onClick={event => {
                          event.stopPropagation();
                          if (state.isReadOnly) return;
                          setEditingBoxlessId(current => (current === task.id ? null : task.id));
                        }}
                        className={cn(
                          'rounded-md border px-2 py-1',
                          !state.isReadOnly && 'cursor-pointer',
                          isEditing
                            ? 'self-start w-max min-w-full border-blue-300 ring-2 ring-blue-100'
                            : inconsistentTaskIds.has(task.id)
                              ? 'border-red-400 ring-1 ring-red-200 dark:ring-red-900'
                              : 'border-slate-200 dark:border-slate-600 bg-white/95 dark:bg-card/95',
                        )}
                      >
                        {/* Same header (editable path + task-type dropdown + points + delete) as the on-page
                            cards, just without the OCR text area below (these tasks have no region). Click to
                            edit, like the on-page cards (which focus via their box). */}
                        <TaskCardHeader
                          store={store}
                          task={task}
                          isEditable={isEditing}
                          missing={inconsistentTaskIds.has(task.id)}
                          visibleTaskTypes={visibleTaskTypes as TaskType[]}
                          onDelete={() => {
                            const next = state.tasks.filter(other => other.id !== task.id);
                            refs.tasksRef.current = next;
                            actions.setTasks(next);
                            actions.requestPersist(next);
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              );
            })()}
            {pagesWithLayout.map(({ pageIndex, pageHeightPx, positionedPageBoxes }) => (
              <div key={pageIndex} className="relative w-full min-w-0 bg-transparent">
                {pageIndex > 0 && <div className="mb-2 h-px w-full bg-slate-300/80" />}

                <div
                  className="relative w-full overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white/90 dark:bg-card/90 pr-1"
                  style={{ height: `${pageHeightPx}px` }}
                  onPointerDown={event => event.stopPropagation()}
                >
                  {positionedPageBoxes.length === 0 ? (
                    <div className="flex h-full items-center justify-center px-3 text-center text-xs text-slate-500 dark:text-slate-400">
                      {t('Canvas.structureEditor.noTasksOnPage')}
                    </div>
                  ) : (
                    positionedPageBoxes.map(item => {
                      const isFocused = state.selectedBoxKey === item.boxKey;

                      if (item.kind === 'task') {
                        const { task, box, boxKey, cardTop } = item;
                        const isEditable = isFocused && !state.isReadOnly;
                        return (
                          <div
                            key={`${boxKey}-card`}
                            className={cn(
                              'absolute left-0 px-1 pointer-events-auto',
                              isEditable ? 'w-max min-w-full' : 'right-0',
                              isFocused ? 'z-50' : 'z-30',
                            )}
                            style={{ top: `${cardTop}%` }}
                          >
                            <div
                              onPointerDown={event => event.stopPropagation()}
                              onClick={event => {
                                event.stopPropagation();
                                actions.selectBox(task.id, boxKey);
                              }}
                              className={cn(
                                'flex w-full flex-col rounded-md border bg-white/95 dark:bg-card/95 px-2 py-1 text-left shadow-sm transition-colors cursor-pointer',
                                isFocused
                                  ? 'border-blue-300 ring-2 ring-blue-100'
                                  : inconsistentTaskIds.has(task.id)
                                    ? 'border-red-400 ring-2 ring-red-200 dark:ring-red-900'
                                    : getConfidenceCardClasses(box.confidence),
                              )}
                              ref={element => {
                                if (element) {
                                  refs.cardRefsMap.current.set(boxKey, element);
                                } else {
                                  refs.cardRefsMap.current.delete(boxKey);
                                }
                                if (!element) return;
                                const nextHeight = Math.ceil(element.getBoundingClientRect().height);
                                actions.setCardHeightsPx(prev => {
                                  if (prev[boxKey] === nextHeight) return prev;
                                  return { ...prev, [boxKey]: nextHeight };
                                });
                              }}
                            >
                              <TaskCardHeader
                                store={store}
                                task={task}
                                isEditable={isEditable}
                                missing={inconsistentTaskIds.has(task.id)}
                                visibleTaskTypes={visibleTaskTypes as TaskType[]}
                                onDelete={() => actions.removeBox(task.id, box.id)}
                              />
                              {!shouldShowOcrText(box.contentType) ? (
                                <div className="mt-1 flex items-center gap-1.5 text-[11px] font-medium italic text-slate-500 dark:text-slate-400">
                                  <SquareDashedText size={12} />
                                  {t('Canvas.structureEditor.imageContent')}
                                </div>
                              ) : isEditable ? (
                                <div
                                  onPointerDown={event => event.stopPropagation()}
                                  onClick={event => event.stopPropagation()}
                                >
                                  <LatexRichEditor
                                    value={box.ocrExtractedText || ''}
                                    onChange={value => actions.updateBoxText(task.id, box.id, value)}
                                    examId={store.props.exam?._id}
                                    onKeyDown={event => {
                                      if (event.key === 'Enter' && !event.shiftKey) {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        actions.clearSelection();
                                      }
                                    }}
                                    className="mt-1 min-h-14 border-slate-200 dark:border-slate-600 focus-within:border-sky-400 text-[11px]"
                                    placeholder={t('Canvas.structureEditor.noText')}
                                  />
                                </div>
                              ) : (
                                <div
                                  className={cn(
                                    'mt-1 text-[11px] font-medium text-slate-700 dark:text-slate-300',
                                    isFocused ? 'whitespace-pre-wrap break-words' : 'truncate',
                                  )}
                                >
                                  <MarkdownWithMath>
                                    {box.ocrExtractedText || t('Canvas.structureEditor.noText')}
                                  </MarkdownWithMath>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      } else {
                        const { info, boxKey, referencedTaskPaths, cardTop } = item;
                        return (
                          <div
                            key={`${boxKey}-card`}
                            className={cn(
                              'absolute left-0 right-0 px-1 pointer-events-auto',
                              isFocused ? 'z-50' : 'z-30',
                            )}
                            style={{ top: `${cardTop}%` }}
                          >
                            <div
                              onPointerDown={event => event.stopPropagation()}
                              onClick={event => {
                                event.stopPropagation();
                                actions.selectAdditionalInfoBox(info.id, boxKey);
                              }}
                              className={cn(
                                'flex w-full flex-col rounded-md border bg-white/95 dark:bg-card/95 px-2 py-1 text-left shadow-sm transition-colors cursor-pointer',
                                isFocused
                                  ? 'border-purple-300 ring-2 ring-purple-100 dark:ring-purple-900'
                                  : 'border-purple-200 dark:border-purple-800 bg-purple-50/40 dark:bg-purple-900/10 hover:border-purple-300 dark:hover:border-purple-700',
                              )}
                              ref={element => {
                                if (element) {
                                  refs.cardRefsMap.current.set(boxKey, element);
                                } else {
                                  refs.cardRefsMap.current.delete(boxKey);
                                }
                                if (!element) return;
                                const nextHeight = Math.ceil(element.getBoundingClientRect().height);
                                actions.setCardHeightsPx(prev => {
                                  if (prev[boxKey] === nextHeight) return prev;
                                  return { ...prev, [boxKey]: nextHeight };
                                });
                              }}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-[11px] font-semibold text-purple-700 dark:text-purple-400 truncate">
                                  {t('Canvas.structureEditor.additionalInfo')}
                                </span>
                                <div className="flex items-center gap-1.5 shrink-0">
                                  {!isFocused && referencedTaskPaths.length > 0 && (
                                    <span className="rounded bg-purple-100 dark:bg-purple-900/30 px-1.5 py-0.5 text-[9px] font-semibold text-purple-700 dark:text-purple-400 whitespace-nowrap">
                                      {referencedTaskPaths.map(p => `A${p.join('.')}`).join(', ')}
                                    </span>
                                  )}
                                  {!state.isReadOnly && (
                                    <button
                                      type="button"
                                      className="rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary p-1 text-slate-600 dark:text-slate-400 transition-colors hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600"
                                      onPointerDown={event => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        actions.removeAdditionalInfoBox(info.id, item.box.id);
                                      }}
                                      title={t('Canvas.structureEditor.deleteBbx')}
                                    >
                                      <Trash2 size={11} />
                                    </button>
                                  )}
                                </div>
                              </div>
                              {!shouldShowOcrText(item.box.contentType) ? (
                                <div className="mt-1 flex items-center gap-1.5 text-[11px] font-medium italic text-slate-500 dark:text-slate-400">
                                  <SquareDashedText size={12} />
                                  {t('Canvas.structureEditor.imageContent')}
                                </div>
                              ) : (
                                <div
                                  className={cn(
                                    'mt-1 text-[11px] font-medium text-slate-700',
                                    isFocused ? 'whitespace-pre-wrap break-words' : 'truncate',
                                  )}
                                >
                                  <MarkdownWithMath>
                                    {info.content || t('Canvas.structureEditor.noContent')}
                                  </MarkdownWithMath>
                                </div>
                              )}
                              {isFocused && (
                                <div className="mt-1.5 flex flex-col gap-0.5">
                                  <span className="text-[10px] font-semibold uppercase tracking-wide text-purple-600 dark:text-purple-400">
                                    {t('Canvas.structureEditor.linkedTasks')}
                                  </span>
                                  <div className="max-h-32 overflow-y-auto flex flex-col gap-0.5">
                                    {state.tasks.length === 0 ? (
                                      <span className="text-[11px] text-slate-400 dark:text-slate-500">
                                        {t('Canvas.structureEditor.noTasks')}
                                      </span>
                                    ) : (
                                      state.tasks.map(tTask => {
                                        const tPath = Array.isArray(tTask.path) ? tTask.path : [];
                                        const isChecked =
                                          Array.isArray(tTask.additionalInfoIds) &&
                                          tTask.additionalInfoIds.includes(info.id);
                                        return (
                                          <label
                                            key={tTask.id}
                                            className="flex items-center gap-1.5 cursor-pointer rounded px-1 py-0.5 hover:bg-purple-50 dark:hover:bg-purple-900/20"
                                            onPointerDown={e => e.stopPropagation()}
                                            onClick={e => e.stopPropagation()}
                                          >
                                            <input
                                              type="checkbox"
                                              checked={isChecked}
                                              onChange={e =>
                                                actions.updateAdditionalInfoTaskAssignment(
                                                  info.id,
                                                  tTask.id,
                                                  e.target.checked,
                                                )
                                              }
                                              className="h-3 w-3 cursor-pointer accent-purple-600"
                                              onPointerDown={e => e.stopPropagation()}
                                              onClick={e => e.stopPropagation()}
                                            />
                                            <span className="text-[11px] text-slate-700 dark:text-slate-300">
                                              A{tPath.join('.')}
                                            </span>
                                          </label>
                                        );
                                      })
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      }
                    })
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
